const DEFAULT_DIGITS = 2;

class NumberFormat extends HTMLElement {
    static get is() {
        return "number-format";
    }

    static get properties() {
        return {
            number: String,
            minDigits: {
                attribute: "min-digits"
            },
            maxDigits: {
                attribute: "max-digits"
            },
            currency: String,
            strikethrough: Boolean,
            locale: String
        };
    }

    static get observedAttributes() {
        return ["number", "min-digits", "max-digits", "currency", "locale" ,"strikethrough"];
    }

    constructor() {
        super();
        this.shadow = this.attachShadow({ mode: "open" });
        this.parts = []
        this.locale = this.getAttribute('locale') || document.documentElement.lang || navigator.language;
    }

    formatPrice(){
        if (this.number) {
            let locale = this.locale;

            // Overwrite locale for EUR currency in international stores
            if (this.currency === 'EUR' && this.locale !== 'nl') {
                locale = 'de';
            }

            const numberFormat = new Intl.NumberFormat(locale, this.getLocaleProperties());
            this.parts = numberFormat.formatToParts(this.number);
            const formattedPrice = document.createElement("div");

            // Adjust group separator for EUR and PLN based on conditions
            if ((this.currency === 'EUR' && this.locale !== 'nl') || (this.currency === 'PLN' && this.locale === 'pl')) {
                this.adjustGroupSeparator('.');
            }

            if (this.parts.length && this.parts[0].type == 'currency') {
                formattedPrice.classList.add('currency-before');
            }
            formattedPrice.classList.add(this.strikethrough ? 'strikethrough' : 'normal');

            formattedPrice.innerHTML = this.parts.map(p => {
                let value = p.value
                let cssClass = p.type;
                // keep-zeros: toon ",00" voluit (gebruikt voor de adviesprijs op de PDP)
                if (p.type == 'fraction' && value == '00' && !this.hasAttribute('keep-zeros')) {
                    value = '-';
                    cssClass = 'fraction-removed'
                }
                return `<span part="${p.type}" class="${cssClass}">${value}</span>`;
            }).join('');
            if (this.shadow.querySelector('div')) {
                this.shadow.querySelector('div').replaceWith(formattedPrice);
            }
            else {
                this.shadow.appendChild(formattedPrice);
            }
        }
    }

    // Adjusts the group separator for specific formatting requirements.
    adjustGroupSeparator(separator) {
        this.parts = this.parts.map(part => {
            if (part.type === 'group') {
                part.value = separator;
            }
            return part;
        });

        // Additional logic for PLN to ensure separator applies for all numbers
        if (this.currency === 'PLN' && this.locale === 'pl') {
            this.parts = this.parts.map(part => {
                if (part.type === 'integer') {
                    const value = part.value.replace(/(\d)(?=(\d{3})+$)/g, `$1${separator}`);
                    part.value = value;
                }
                return part;
            });
        }
    }

    connectedCallback() {
        const style = document.createElement("style");
        style.textContent = `
            :host{
                --decimal-size: 0.5em;
                --decimal-vertical-align: super;
                --strikethrough-rotate: -15deg;
                --strikethrough-color: rgb(69, 69, 69);
                --strikethrough-width: 1px;
                --strikethrough-offset-left: 1em;
                --strikethrough-offset-right: 1em;
                display: inline-block;
                line-height: 1;
            }
            div.normal {
                display: inline-block;
                line-height: 1;
            }
            div.strikethrough{
                position: relative;
                display: inline-block;
                line-height: 1;
            }
            div.strikethrough::before {
              position: absolute;
              left: 0;
              top: 50%;
              right: var(--strikethrough-offset-right);
              border-top-style: solid;
              border-top-width: var(--strikethrough-width);
              border-color: var(--strikethrough-color);
              content: " ";
              transform: rotate(var(--strikethrough-rotate));
            }
            div.strikethrough.currency-before::before {
                left: var(--strikethrough-offset-left);
                right: 0;
            }
            span.fraction {
                vertical-align: var(--decimal-vertical-align);
                font-size: var(--decimal-size);
            }
    `;
        this.shadow.appendChild(style);
    }

    attributeChangedCallback(attrName, oldVal, newVal) {
        if (attrName === 'number') {
            this.number = newVal;
        }
        if (attrName === 'locale') {
            this.locale = newVal;
        }
        if (attrName === 'currency') {
            this.currency = newVal;
        }
        if (attrName === 'strikethrough') {
            this.strikethrough = newVal;
        }
        this.formatPrice();
    }

    getLocaleProperties() {
        const {currency} = this;
        return {
            minimumFractionDigits: this._getIntAttribute("min-digits"),
            maximumFractionDigits: this._getIntAttribute("max-digits"),
            style: this.getStyle(),
            currency
        };
    }

    getStyle() {
        return this.currency !== void 0 ? "currency" : "decimal";
    }

    _getIntAttribute(name) {
        const value = parseInt(this.getAttribute(name));
        return isNaN(value) ? DEFAULT_DIGITS : value;
    }
}


const debounce = (fn, time) => {
    let key;
    return function() {
        const functionCall = () => fn.apply(this, arguments);
        clearTimeout(key);
        key = setTimeout(functionCall, time);
    }
};

customElements.define(NumberFormat.is, NumberFormat);
